import React, { useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getTopicIcon } from './TopicItem';
import { TOPIC_COLORS } from './topicColors';
import { useViewport } from '../../contexts/ViewportContext';
import { useTopicSkeletonState } from '../../hooks/useTopicSkeletonState';
import { CollapsedTopicSkeleton } from './CollapsedTopicSkeleton';
import { logger } from '../../utils/logger';
import { useTopicFetching } from '../../hooks/useTopicFetching';
import { useTopicUIStore } from '../../store/topicUIStore';

interface CollapsedTopicListProps {
  onTopicSelect: (topicId: string) => void;
  onExpand: () => void;
  onNewTopic: () => void;
  projectId: string;
}

export function CollapsedTopicList({
  onTopicSelect,
  onNewTopic,
  projectId,
}: CollapsedTopicListProps) {
  const { activeTopicId } = useTopicUIStore();
  const [hoveredTopic, setHoveredTopic] = useState<string | null>(null);
  const [isListHovered, setIsListHovered] = useState(false);
  const { isMobile } = useViewport();

  // Use the enhanced topic fetching hook for validated topics
  const { topics, loading: isLoading, fetching: isFetching } = useTopicFetching(projectId);

  // Use the unified skeleton state hook
  const { loading: skeletonLoading } = useTopicSkeletonState(projectId);

  // Combine loading states
  const loading = isLoading || skeletonLoading;

  // Background fetching indicator (data is available but refreshing)
  const isBackgroundFetching = !isLoading && isFetching;

  // Only show for desktop
  if (isMobile) {
    return null;
  }

  const handleTopicClick = (topicId: string) => {
    logger.component('CollapsedTopicList: Topic clicked', { topicId });
    onTopicSelect(topicId);
  };

  return (
    <div
      className="h-full flex flex-col w-full overflow-hidden relative"
      onMouseEnter={() => setIsListHovered(true)}
      onMouseLeave={() => setIsListHovered(false)}
      data-loading={loading ? 'true' : 'false'}
      data-fetching={isBackgroundFetching ? 'true' : 'false'}
    >
      {/* Header with new topic button */}
      <div className="h-14 flex items-center justify-center relative">
        <button
          onClick={onNewTopic}
          className="w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 rounded-full flex items-center justify-center transition-all shadow-sm"
          title="New topic"
          aria-label="Create new topic"
        >
          <Plus size={18} />
        </button>

        {/* Background fetching indicator */}
        {isBackgroundFetching && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <RefreshCw size={16} className="text-blue-500 animate-spin opacity-70" />
          </div>
        )}
      </div>

      {/* Topics list with scrolling */}
      <div
        className="flex-1 overflow-y-auto"
        style={{
          height: 'calc(100vh - 120px)',
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <CollapsedTopicSkeleton />
        ) : (
          <div className="flex flex-col items-center gap-4 py-4">
            {topics.map(topic => {
              const Icon = getTopicIcon(topic.topicType);
              const isActive = topic.id === activeTopicId;

              return (
                <div
                  key={topic.id}
                  className="relative"
                  onMouseEnter={() => setHoveredTopic(topic.id)}
                  onMouseLeave={() => setHoveredTopic(null)}
                >
                  <button
                    onClick={() => handleTopicClick(topic.id)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
                    title={topic.name}
                    aria-label={`Open ${topic.name} topic`}
                  >
                    <div
                      className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center',
                        isActive
                          ? TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]?.active ||
                              TOPIC_COLORS.default.active
                          : isListHovered
                            ? TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]?.bgHover ||
                              TOPIC_COLORS.default.bgHover
                            : TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]
                                ?.collapsedBg || TOPIC_COLORS.default.collapsedBg
                      )}
                    >
                      {Icon && (
                        <Icon
                          size={18}
                          className={cn(
                            isActive
                              ? 'text-white'
                              : isListHovered
                                ? TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]
                                    ?.icon || TOPIC_COLORS.default.icon
                                : TOPIC_COLORS[topic.topicType as keyof typeof TOPIC_COLORS]
                                    ?.collapsedIcon || TOPIC_COLORS.default.collapsedIcon
                          )}
                        />
                      )}
                    </div>
                  </button>

                  {/* Topic name tooltip on hover */}
                  {hoveredTopic === topic.id && (
                    <div
                      className="fixed ml-2 bg-white text-gray-800 text-sm py-1.5 px-3 rounded-md shadow-lg whitespace-nowrap z-40 border border-gray-200 font-medium"
                      style={{ left: '100px', marginTop: '-32px' }}
                    >
                      {topic.name}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom spacing - removed new topic button as it's now at the top */}
      <div className="p-3 flex justify-center"></div>
    </div>
  );
}
